/* Avora Rewards — pure reward math shared by the storefront and QA tests. */
(function (root, factory) {
  'use strict';

  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.AvoraRewardsCore = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function isRewardGift(item) {
    return !!(item && item.properties && (item.properties._avora_reward_gift === true || item.properties._avora_reward_gift === 'true'));
  }

  function rewardGiftParentKey(item) {
    return item && item.parent_relationship && item.parent_relationship.parent_key
      ? String(item.parent_relationship.parent_key)
      : '';
  }

  function isExcludedProduct(item, excludedProducts) {
    var productId = item && item.product_id != null ? String(item.product_id) : '';
    var variantId = item && item.variant_id != null ? String(item.variant_id) : '';
    return !!(excludedProducts && (
      (productId && excludedProducts[productId] === true)
      || (variantId && excludedProducts['variant:' + variantId] === true)
    ));
  }

  function isEligibleMerchandise(item, excludedProducts) {
    if (!item || Number(item.quantity) <= 0) return false;
    if (item.gift_card === true || item.is_gift_card === true) return false;
    if (item.selling_plan_allocation || item.selling_plan) return false;
    if (isExcludedProduct(item, excludedProducts)) return false;
    var properties = item.properties || {};
    if (properties._avora_reward_excluded === true || properties._avora_reward_excluded === 'true') return false;
    return true;
  }

  function isEligiblePaidItem(item, excludedProducts) {
    return isEligibleMerchandise(item, excludedProducts)
      && !isRewardGift(item)
      && !(item.parent_relationship && item.parent_relationship.parent_key)
      && !!item.key
      && (Number(item._avora_net_line_price != null ? item._avora_net_line_price : item.final_line_price) || 0) > 0;
  }

  function managedWalletCodes(walletCodes) {
    return Object.keys(walletCodes || {}).map(function (key) {
      return String(walletCodes[key] || '').toLowerCase();
    }).filter(Boolean);
  }

  function rewardGiftWalletDiscount(item, walletCodes) {
    var managed = managedWalletCodes(walletCodes);
    return (item && item.line_level_discount_allocations || []).reduce(function (total, allocation) {
      var application = allocation && allocation.discount_application;
      var title = application && application.type === 'discount_code' ? String(application.title || '').toLowerCase() : '';
      var amount = allocation && allocation.amount != null ? allocation.amount : allocation && allocation.discounted_amount;
      return total + (managed.indexOf(title) === -1 ? 0 : (Number(amount) || 0));
    }, 0);
  }

  function rewardGiftLineDiscount(item) {
    if (item && item.original_line_price != null && item.final_line_price != null) {
      return Math.max(0, (Number(item.original_line_price) || 0)
        - (Number(item._avora_net_line_price != null ? item._avora_net_line_price : item.final_line_price) || 0));
    }
    return (item && item.line_level_discount_allocations || []).reduce(function (total, allocation) {
      var amount = allocation && allocation.amount != null ? allocation.amount : allocation && allocation.discounted_amount;
      return total + (Number(amount) || 0);
    }, 0);
  }

  function rewardGiftLineValue(item, walletCodes) {
    if (item && item.original_line_price != null) return Number(item.original_line_price) || 0;
    return (Number(item && item.final_line_price) || 0) + rewardGiftLineDiscount(item);
  }

  function calculateState(currentCart, tiers, walletCodes, excludedProducts) {
    var paidSubtotal = 0;
    var giftTotal = 0;
    var giftDiscountTotal = 0;
    var walletDiscountTotal = 0;
    var paidCount = 0;
    var giftItems = [];
    var invalidGiftItems = [];
    var validPaidParents = {};

    (currentCart.items || []).forEach(function (item) {
      if (isEligiblePaidItem(item, excludedProducts)) validPaidParents[String(item.key)] = item;
    });

    (currentCart.items || []).forEach(function (item) {
      if (isRewardGift(item)) {
        giftItems.push(item);
        var parent = validPaidParents[rewardGiftParentKey(item)];
        var differentVariant = !parent
          || parent.variant_id == null
          || item.variant_id == null
          || String(parent.variant_id) !== String(item.variant_id);
        if (parent
          && differentVariant
          && isEligibleMerchandise(item, excludedProducts)
          && rewardGiftLineValue(item, walletCodes) > 0) {
          giftTotal += rewardGiftLineValue(item, walletCodes);
          giftDiscountTotal += rewardGiftLineDiscount(item);
          walletDiscountTotal += rewardGiftWalletDiscount(item, walletCodes);
        } else invalidGiftItems.push(item);
      } else if (isEligiblePaidItem(item, excludedProducts)) {
        paidSubtotal += item._avora_net_line_price != null
          ? Math.max(0, Number(item._avora_net_line_price) || 0)
          : Number(item.final_line_price) || 0;
        paidCount += Number(item.quantity) || 0;
      }
    });

    var currentTier = null;
    var nextTier = null;
    (tiers || []).forEach(function (tier) {
      if (paidSubtotal >= tier.threshold) currentTier = tier;
      else if (!nextTier) nextTier = tier;
    });

    var wallet = currentTier ? currentTier.wallet : 0;
    var futureCard = currentTier && currentTier.futureCard ? currentTier.futureCard : 0;
    var futureMinimum = currentTier && currentTier.futureMinimum ? currentTier.futureMinimum : 0;
    var credit = Math.min(wallet, giftTotal);
    var walletReady = wallet > 0 && giftTotal > 0;
    var appliedCredit = credit;
    var walletRemaining = Math.max(0, wallet - appliedCredit);
    var pendingWalletAdjustment = Math.max(0, appliedCredit - giftDiscountTotal);

    return {
      paidSubtotal: paidSubtotal,
      paidCount: paidCount,
      giftTotal: giftTotal,
      giftDiscountTotal: giftDiscountTotal,
      walletDiscountTotal: walletDiscountTotal,
      giftItems: giftItems,
      invalidGiftItems: invalidGiftItems,
      currentTier: currentTier,
      nextTier: nextTier,
      wallet: wallet,
      credit: credit,
      walletReady: walletReady,
      appliedCredit: appliedCredit,
      walletRemaining: walletRemaining,
      pendingWalletAdjustment: pendingWalletAdjustment,
      topup: Math.max(0, giftTotal - wallet),
      futureCard: futureCard,
      futureMinimum: futureMinimum,
      estimatedTotal: Math.max(0, (Number(currentCart.total_price) || 0) - pendingWalletAdjustment)
    };
  }

  function canChangeGift(state, currentQuantity, nextQuantity) {
    var from = Math.max(0, Number(currentQuantity) || 0);
    var to = Math.max(0, Number(nextQuantity) || 0);
    if (to <= from) return true;
    return !!(state && Number(state.walletRemaining) > 0);
  }

  function ladderProgress(paidSubtotal, tiers) {
    var highestThreshold = (tiers || []).reduce(function (highest, tier) {
      return Math.max(highest, Number(tier && tier.threshold) || 0);
    }, 0);

    if (!highestThreshold) return 0;
    return Math.max(0, Math.min(100, ((Number(paidSubtotal) || 0) / highestThreshold) * 100));
  }

  function canIncreaseGift(state) {
    return !!(state && Number(state.walletRemaining) >= 1);
  }

  function shouldShowProductGift(state, selectedQuantity, eligible, available) {
    return Number(selectedQuantity) > 0 || (!!eligible && !!available && canIncreaseGift(state));
  }

  function rewardCode(state, walletCodes, skipWallet) {
    var codeWallet = state && state.currentTier && state.currentTier.baseWallet != null
      ? state.currentTier.baseWallet
      : state && state.wallet;
    if (!skipWallet && state.walletReady) return (walletCodes || {})[String(codeWallet)] || '';
    return '';
  }

  function discountCodeTitles(currentCart) {
    var codes = [];

    function add(application) {
      if (!application || application.type !== 'discount_code' || !application.title) return;
      var title = String(application.title).trim();
      if (title && !codes.some(function (code) { return code.toLowerCase() === title.toLowerCase(); })) codes.push(title);
    }

    (currentCart.cart_level_discount_applications || []).forEach(add);
    (currentCart.discount_codes || []).forEach(function (discount) {
      if (discount && discount.code) add({ type: 'discount_code', title: discount.code });
    });
    (currentCart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        add(allocation && allocation.discount_application);
      });
    });
    return codes;
  }

  function checkoutDiscountCodes(currentCart, state, walletCodes, skipWallet) {
    var managed = managedWalletCodes(walletCodes);
    var codes = discountCodeTitles(currentCart).filter(function (code) {
      return managed.indexOf(code.toLowerCase()) === -1;
    });
    var reward = rewardCode(state, walletCodes, skipWallet);
    if (reward && !codes.some(function (code) { return code.toLowerCase() === reward.toLowerCase(); })) codes.push(reward);
    return codes;
  }

  function checkoutUrl() {
    return '/checkout';
  }

  function requiresGiftDecision(state) {
    return !!((!state.wallet && state.giftTotal) || (state.invalidGiftItems && state.invalidGiftItems.length));
  }

  function checkoutLink(state) {
    return requiresGiftDecision(state) ? '/cart#avora-gift-wallet' : '/checkout';
  }

  function euros(cents) {
    return ((Number(cents) || 0) / 100).toFixed(2);
  }

  function baseMinor(presentmentMinor, currencyRate) {
    var rate = Number(currencyRate);
    if (!Number.isFinite(rate) || rate <= 0) rate = 1;
    return Math.round((Number(presentmentMinor) || 0) / rate);
  }

  function tierBaseValue(tier, key) {
    if (!tier) return 0;
    var baseKey = 'base' + key.charAt(0).toUpperCase() + key.slice(1);
    return tier[baseKey] != null ? Number(tier[baseKey]) || 0 : Number(tier[key]) || 0;
  }

  function rewardAttributes(state, configVersion, currencyRate) {
    if (configVersion === "2026-09-15.1" || configVersion === "2026-09-15.2") currencyRate = 1;
    var paidSubtotal = baseMinor(state.paidSubtotal, currencyRate);
    var offerValue = function (key) { return configVersion === '2026-09-15.2' ? Number(state.currentTier && state.currentTier[key]) || 0 : tierBaseValue(state.currentTier, key); };
    var tierThreshold = offerValue('threshold');
    var wallet = state.giftTotal > 0 ? offerValue('wallet') : 0;
    var futureCard = offerValue('futureCard');
    var futureMinimum = offerValue('futureMinimum');
    return {
      'Avora Rewards Version': '4',
      'Avora Rewards Config Version': String(configVersion || ''),
      'Avora Paid Subtotal': euros(paidSubtotal),
      'Avora Reward Tier': tierThreshold ? euros(tierThreshold) : '',
      'Avora Gift Wallet': wallet ? euros(wallet) : '',
      'Avora Reward Card': futureCard ? euros(futureCard) : '',
      'Avora Reward Card Minimum': futureMinimum ? euros(futureMinimum) : ''
    };
  }

  function normaliseSearch(value) {
    var normalised = String(value || '').toLowerCase();
    if (typeof normalised.normalize === 'function') {
      normalised = normalised.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    return normalised.replace(/\s+/g, ' ').trim();
  }

  function productSearchScore(product, query) {
    var normalisedQuery = normaliseSearch(query);
    if (!normalisedQuery) return 0;

    var title = normaliseSearch(product && product.title);
    var titleWords = title.split(/[\s\-–—_/]+/).filter(Boolean);
    var queryWords = normalisedQuery.split(' ').filter(Boolean);
    var allQueryWordsInTitle = queryWords.every(function (word) { return title.indexOf(word) !== -1; });

    if (title === normalisedQuery) return 0;
    if (title.indexOf(normalisedQuery) === 0) return 1;
    if (titleWords.some(function (word) { return word.indexOf(normalisedQuery) === 0; })) return 2;
    if (title.indexOf(normalisedQuery) !== -1) return 3;
    if (allQueryWordsInTitle) return 4;

    var searchText = normaliseSearch(product && product.searchText);
    var allQueryWordsInMetadata = queryWords.every(function (word) { return searchText.indexOf(word) !== -1; });
    if (searchText.indexOf(normalisedQuery) !== -1 || allQueryWordsInMetadata) return 5;
    return -1;
  }

  return {
    isRewardGift: isRewardGift,
    isEligibleMerchandise: isEligibleMerchandise,
    isEligiblePaidItem: isEligiblePaidItem,
    rewardGiftParentKey: rewardGiftParentKey,
    rewardGiftLineValue: rewardGiftLineValue,
    calculateState: calculateState,
    canChangeGift: canChangeGift,
    ladderProgress: ladderProgress,
    canIncreaseGift: canIncreaseGift,
    shouldShowProductGift: shouldShowProductGift,
    rewardCode: rewardCode,
    discountCodeTitles: discountCodeTitles,
    checkoutDiscountCodes: checkoutDiscountCodes,
    checkoutUrl: checkoutUrl,
    checkoutLink: checkoutLink,
    requiresGiftDecision: requiresGiftDecision,
    rewardAttributes: rewardAttributes,
    baseMinor: baseMinor,
    normaliseSearch: normaliseSearch,
    productSearchScore: productSearchScore
  };
});
